import { useCallback, useRef, useState } from "react";
import type { TransferFileStatus } from "@airspace/shared";
import type { BulkDownloadState, TransferEntry } from "../api/useTransfers.js";
import { supportsDirectSave } from "../api/transfer.js";
import { Button, Icons, Progress } from "../design/components/index.js";

export interface TransferPanelProps {
  files: TransferEntry[];
  bulk: BulkDownloadState;
  actions: {
    offer: (files: File[]) => Promise<void>;
    accept: (fileId: string) => Promise<void>;
    reject: (fileId: string) => Promise<void>;
    cancel: (fileId: string) => Promise<void>;
    pause: (fileId: string) => void;
    resume: (fileId: string) => void;
    download: (file: TransferEntry) => Promise<void>;
    downloadAll: () => Promise<unknown>;
    canResumeUpload: (fileId: string) => boolean;
  };
}

/** Formatiert Bytes mit deutschem Dezimalkomma. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  const units = ["kB", "MB", "GB", "TB"];
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1).replace(".", ",")} ${units[unit]}`;
}

const STATUS_TEXT: Record<TransferFileStatus, string> = {
  offered: "Wartet auf Zustimmung",
  accepted: "Angenommen",
  uploading: "Überträgt",
  verifying: "Prüfsumme wird berechnet",
  complete: "Bereit",
  rejected: "Abgelehnt",
  cancelled: "Zurückgezogen",
  failed: "Fehlgeschlagen",
  expired: "Abgelaufen",
};

export function TransferPanel({ files, bulk, actions }: TransferPanelProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) return;
      void actions.offer(Array.from(list));
    },
    [actions],
  );

  const incoming = files.filter((file) => file.direction === "incoming");
  const outgoing = files.filter((file) => file.direction === "outgoing");
  const pendingOffers = incoming.filter((file) => file.status === "offered");
  const readyToSave = incoming.filter((file) => file.status === "complete");
  const readyBytes = readyToSave.reduce((sum, file) => sum + file.size, 0);

  return (
    <div>
      {/*
        Ablagefläche. Sie ist zugleich ein Button, damit sie ohne Maus und ohne
        Drag-and-drop bedienbar bleibt -- Ziehen ist per Tastatur nicht möglich.
      */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
        style={{
          width: "100%",
          padding: "var(--space-6) var(--space-4)",
          background: dragging ? "var(--color-lifted)" : "transparent",
          border: `1.5px dashed ${dragging ? "var(--color-ink)" : "var(--color-dust)"}`,
          borderRadius: "var(--radius-stadium)",
          color: "var(--color-granite)",
          cursor: "pointer",
          transition: "background var(--motion-fast) var(--motion-ease)",
        }}
      >
        <Icons.Upload size={28} />
        <div style={{ marginTop: "var(--space-1)", fontWeight: "var(--weight-display)", color: "var(--color-ink)" }}>
          Dateien auswählen oder hierher ziehen
        </div>
        <div style={{ marginTop: 4, fontSize: "var(--text-small)" }}>
          Das andere Gerät muss jede Datei einzeln annehmen.
        </div>
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="visually-hidden"
        onChange={(event) => {
          handleFiles(event.target.files);
          // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
          event.target.value = "";
        }}
      />

      {pendingOffers.length > 1 ? (
        <div style={{ marginTop: "var(--space-3)" }}>
          <Button
            variant="secondary"
            onClick={() => {
              for (const file of pendingOffers) void actions.accept(file.fileId);
            }}
          >
            Alle {pendingOffers.length} annehmen
          </Button>
        </div>
      ) : null}

      {/*
        Sammeldownload erst ab zwei Dateien: bei einer einzigen ist der Knopf an
        der Datei selbst der kürzere Weg, und ein Archiv wäre nur zusätzliche
        Verpackung.
      */}
      {readyToSave.length > 1 ? (
        <div style={{ marginTop: "var(--space-3)" }}>
          <Button onClick={() => void actions.downloadAll()} disabled={bulk.running}>
            <Icons.Download size={18} />
            {bulk.running
              ? "Archiv wird erstellt…"
              : `Alle ${readyToSave.length} herunterladen (${formatBytes(readyBytes)})`}
          </Button>

          {bulk.running ? (
            <div style={{ marginTop: "var(--space-2)", maxWidth: "26rem" }}>
              <Progress
                value={bulk.totalBytes > 0 ? bulk.receivedBytes / bulk.totalBytes : undefined}
                label="Archiv wird gespeichert"
                detail={`${formatBytes(bulk.receivedBytes)} von ungefähr ${formatBytes(bulk.totalBytes)}`}
              />
            </div>
          ) : null}

          {bulk.error ? (
            <p
              role="alert"
              style={{
                marginTop: "var(--space-1)",
                fontSize: "var(--text-small)",
                color: "var(--color-danger)",
              }}
            >
              {bulk.error}
            </p>
          ) : null}
        </div>
      ) : null}

      {incoming.length > 0 ? (
        <FileSection title="Empfangen" files={incoming} actions={actions} />
      ) : null}
      {outgoing.length > 0 ? (
        <FileSection title="Gesendet" files={outgoing} actions={actions} />
      ) : null}

      {files.length === 0 ? (
        <p
          style={{
            marginTop: "var(--space-4)",
            fontSize: "var(--text-small)",
            color: "var(--color-granite)",
          }}
        >
          Noch keine Dateien. Übertragene Dateien werden nach Ablauf der Aufbewahrungszeit
          automatisch vom Server gelöscht.
        </p>
      ) : null}
    </div>
  );
}

function FileSection({
  title,
  files,
  actions,
}: {
  title: string;
  files: TransferEntry[];
  actions: TransferPanelProps["actions"];
}): React.ReactElement {
  return (
    <section style={{ marginTop: "var(--space-4)" }}>
      <h3 style={{ fontSize: "var(--text-small)", fontWeight: "var(--weight-bold)", letterSpacing: "var(--tracking-eyebrow)", textTransform: "uppercase", color: "var(--color-granite)" }}>
        {title}
      </h3>
      <ul style={{ listStyle: "none", margin: "var(--space-2) 0 0", padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {files.map((file) => (
          <li key={file.fileId}>
            <FileRow file={file} actions={actions} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function FileRow({
  file,
  actions,
}: {
  file: TransferEntry;
  actions: TransferPanelProps["actions"];
}): React.ReactElement {
  const isOutgoing = file.direction === "outgoing";
  const upload = file.upload;

  // Beim Senden zählt der eigene Fortschritt, beim Empfangen der vom Server
  // gemeldete Stand -- der Empfänger sieht nur, was tatsächlich angekommen ist.
  const transferred = isOutgoing ? (upload?.sentBytes ?? file.receivedBytes) : file.receivedBytes;
  const active = ["accepted", "uploading", "verifying"].includes(file.status);
  const downloading = file.downloadedBytes !== undefined;

  return (
    <div
      style={{
        padding: "var(--space-2) var(--space-3)",
        background: "var(--color-canvas)",
        borderRadius: "var(--radius-consent)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "var(--space-2)", flexWrap: "wrap" }}>
        <span style={{ fontWeight: "var(--weight-display)", wordBreak: "break-all" }}>{file.name}</span>
        <span style={{ fontSize: "var(--text-small)", color: "var(--color-granite)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {formatBytes(file.size)}
        </span>
      </div>

      <div style={{ marginTop: 4, fontSize: "var(--text-small)", color: statusColor(file.status) }}>
        {upload?.state === "paused" ? "Angehalten" : STATUS_TEXT[file.status]}
        {file.status === "complete" && file.sha256 ? (
          <span style={{ color: "var(--color-granite)" }}> · SHA-256 geprüft</span>
        ) : null}
      </div>

      {(active || downloading) && file.size > 0 ? (
        <div style={{ marginTop: "var(--space-2)" }}>
          <Progress
            value={
              file.status === "verifying"
                ? undefined
                : (downloading ? (file.downloadedBytes ?? 0) : transferred) / file.size
            }
            label={downloading ? "Speichert" : isOutgoing ? "Sendet" : "Empfängt"}
            detail={
              file.status === "verifying"
                ? undefined
                : `${formatBytes(downloading ? (file.downloadedBytes ?? 0) : transferred)} von ${formatBytes(file.size)}`
            }
          />
        </div>
      ) : null}

      {file.error ? (
        <p role="alert" style={{ marginTop: "var(--space-1)", fontSize: "var(--text-small)", color: "var(--color-danger)" }}>
          {file.error}
        </p>
      ) : null}

      <div style={{ marginTop: "var(--space-2)", display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
        {!isOutgoing && file.status === "offered" ? (
          <>
            <Button onClick={() => void actions.accept(file.fileId)}>Annehmen</Button>
            <Button variant="danger" onClick={() => void actions.reject(file.fileId)}>
              Ablehnen
            </Button>
          </>
        ) : null}

        {!isOutgoing && file.status === "complete" ? (
          <Button onClick={() => void actions.download(file)} disabled={downloading}>
            {downloading ? "Speichert…" : supportsDirectSave() ? "Speichern unter…" : "Herunterladen"}
          </Button>
        ) : null}

        {isOutgoing && upload?.state === "running" ? (
          <Button variant="secondary" onClick={() => actions.pause(file.fileId)}>
            Pausieren
          </Button>
        ) : null}

        {isOutgoing && (upload?.state === "paused" || upload?.state === "error") ? (
          <Button onClick={() => actions.resume(file.fileId)}>Fortsetzen</Button>
        ) : null}

        {isOutgoing && active ? (
          <Button variant="danger" onClick={() => void actions.cancel(file.fileId)}>
            Zurückziehen
          </Button>
        ) : null}

        {/*
          Nach einem Neuladen ist das File-Objekt weg -- der Browser gibt es
          ohne erneute Auswahl nicht wieder her. Das wird offen gesagt, statt
          eine Schaltfläche anzubieten, die nichts bewirken kann.
        */}
        {isOutgoing && active && !upload && !actions.canResumeUpload(file.fileId) ? (
          <span style={{ fontSize: "var(--text-small)", color: "var(--color-granite)", alignSelf: "center" }}>
            Nach dem Neuladen der Seite muss diese Datei erneut ausgewählt werden.
          </span>
        ) : null}
      </div>
    </div>
  );
}

function statusColor(status: TransferFileStatus): string {
  if (status === "complete") return "var(--color-success)";
  if (["rejected", "cancelled", "failed", "expired"].includes(status)) return "var(--color-danger)";
  return "var(--color-granite)";
}
